import { STORES } from "@larder/shared";
import { StoreMark } from "./StoreMark";

/**
 * Meeting price comparison, once.
 *
 * This used to be a small link in the corner of the buy card saying "show
 * prices near me", which is how you ship a feature nobody finds. Comparing what
 * five shops charge for the same packet is the biggest thing this screen does,
 * and a household that never taps the link never learns it exists.
 *
 * So it is a doorway rather than a footnote: the first time anyone opens a
 * shopping-list line, this stands in front of the screen and has to be
 * answered. It is shown ONCE — answered either way, it never appears again,
 * because a thing you have already decided is not a question.
 *
 * The five logos do most of the explaining. A row of marks people already have
 * on their home screen says "we check these" faster than a sentence naming
 * them, which matters on a screen with a standing rule against paragraphs.
 *
 * "Not now" is a real answer, not a dead end. It prices against the whole of
 * India rather than switching the feature off — see `ANYWHERE_IN_INDIA`.
 */
export function PriceIntro({
  busy,
  onAllow,
  onSkip,
}: {
  busy: boolean;
  onAllow: () => void;
  onSkip: () => void;
}) {
  return (
    <div className="modal-root" data-testid="price-intro">
      {/* Not dismissible by tapping away, unlike the item screen's edit popup.
          There are two answers here and both are one tap; a third, silent way
          out would just mean being asked again on the next line. */}
      <div className="scrim" />
      <div className="popup intro" role="dialog" aria-label="compare prices">
        <div className="intro-marks" aria-hidden="true">
          {STORES.map((s) => (
            <StoreMark key={s.id} id={s.id} />
          ))}
        </div>

        <h3 className="intro-title">Compare prices before you buy</h3>
        <p className="intro-line">
          See what this costs at each shop, and which one is cheapest.
        </p>
        <p className="intro-why">
          Prices change from city to city, so we need to know yours.
        </p>

        <button
          type="button"
          className="primary"
          disabled={busy}
          onClick={onAllow}
          data-testid="intro-allow"
        >
          {busy ? "Finding you…" : "Use my location"}
        </button>
        {/* Quieter, and deliberately not "No thanks": nothing is being turned
            off. Prices still show, for the country rather than the street. */}
        <button
          type="button"
          className="ghost"
          disabled={busy}
          onClick={onSkip}
          data-testid="intro-skip"
        >
          Not now
        </button>
      </div>
    </div>
  );
}
